import css from './WalletTicketDesktop.module.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import DataTestId from 'meta/dataTestId'
import { Breakpoints } from 'meta/layout/breakpoints'
import { IWalletEntityType } from 'meta/pages/v4/wallet'
import { useAppDispatch } from 'store'
import { CheckoutActions } from 'store/pages/checkout'
import { EventActions } from 'store/pages/event'
import { Dates } from 'utils/dates'

import { ButtonRed, ButtonWhiteBlack } from 'components/Actions/Action'
import { GridColumn } from 'components/Layout/Grid'
import Modal from 'components/Layout/Modal'
import Img from 'components/Media/Img'
import AddExtra from 'components/Pages/Wallet/AddExtra'

import TicketDataFetcher from '../../BulkDataFetcher/TicketDataFetcher/TicketDataFetcher'
import UserTickets from './UserTickets'
import { WalletTicketDesktopProps } from './WalletTicketDesktopContainer'

const WalletTicketDesktop: React.FC<WalletTicketDesktopProps> = (props) => {
  const { baseProps, eventData, locationData } = props
  const { walletEventData, isPastEvent } = baseProps
  const { count, type } = walletEventData

  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const [showTickets, setShowTickets] = useState<boolean>(false)
  const [fetchBulkData, setFetchBulkData] = useState<boolean>(false)

  const [showAddExtraBalloon, setShowAddExtraBalloon] = useState<boolean>(false)

  const date = new Date(eventData.dateEvent.date_tz)

  const openCheckout = (codeUrl: string) => {
    setShowAddExtraBalloon(false)
    dispatch(CheckoutActions.init({ codeUrl }))
  }

  const openExtraGoodsDialog = () => {
    dispatch(EventActions.getGoods({ codeUrl: eventData.codeUrl }))
    setShowAddExtraBalloon(true)
  }

  useEffect(() => {
    if (showTickets && !fetchBulkData) {
      setFetchBulkData(true)
    }
  }, [showTickets])

  return (
    <>
      <GridColumn
        gridColumnStart={{ [Breakpoints.laptop]: 2 }}
        gridColumnEnd={{ [Breakpoints.laptop]: 16 }}
        className={classNames(css.walletTicket, { [css.isPastEvent]: isPastEvent && !showTickets })}
        data-testid={`${DataTestId.wallet.ticketsEventDesktop}-${eventData.id}`}
      >
        <div className={css.walletTicket__img}>
          <img src={eventData.assets[0]?.url} />
          <div className={css.walletTicket__ticketsCount}>
            <Img src={`/wallet/tickets_${count >= 3 ? 3 : count}.svg`} />
            <h5
              data-testid={DataTestId.wallet.ticketCount}
              className={classNames(css.count, { [css.multiple]: count >= 2 })}
            >
              {count}
            </h5>
          </div>
        </div>

        <div className={css.walletTicket__details}>
          {type !== IWalletEntityType.event_collection && (
            <div className={css.date}>
              <h4>{Dates.format(date, t('dates:wallet:date'))}</h4>
              <h4 className={css.fontNormal}>&nbsp;/&nbsp;</h4>
              <h4 className={css.fontNormal}>{Dates.format(date, t('dates:common:hoursMinutes'))}</h4>
            </div>
          )}

          {type == IWalletEntityType.event_collection && (
            <div className={css.walletTicket__eventCollection}>
              <span className={css.icon}>
                <Img src="/icons/checkout-season-header-icon.svg" />
              </span>
              <span className={css.text}>{t('wallet:seasonEvent')}</span>
            </div>
          )}

          <h2 className={css.name}>{eventData.name}</h2>

          <div className={css.location_container}>
            <Img src="/icons/location_marker.svg" />
            <h4 className={css.location_text}>{`${locationData.name} / ${locationData.city}`}</h4>
          </div>
        </div>

        <div className={css.walletTicket_actions}>
          <ButtonWhiteBlack
            className={css.walletTicket__seeTickets}
            onClick={() => setShowTickets((prevState) => !prevState)}
            data-testid={DataTestId.wallet.btnSeeTickets}
          >
            <Img src={showTickets ? `/wallet/ticket_hide.svg` : `/wallet/ticket_show.svg`} />
            <h5>{t(showTickets ? 'wallet:hideTickets' : 'wallet:seeTickets')}</h5>
          </ButtonWhiteBlack>

          {!isPastEvent && eventData.goodsServices?.items?.length > 0 && (
            <ButtonRed className={css.walletTicket__addExtra} onClick={openExtraGoodsDialog}>
              <h5>{t('wallet:addExtra')}</h5>
            </ButtonRed>
          )}
        </div>
      </GridColumn>

      {fetchBulkData && <TicketDataFetcher eventId={eventData.id} />}

      {showTickets && <UserTickets walletEventData={walletEventData} eventData={eventData} />}

      {showAddExtraBalloon && (
        <Modal
          closeIconPath="/icons/close_icon_white_smallcross.svg"
          transparent
          show={true}
          handleClose={() => setShowAddExtraBalloon(false)}
        >
          <AddExtra openCheckout={openCheckout} />
        </Modal>
      )}
    </>
  )
}

export default WalletTicketDesktop
